import React, { ReactElement } from 'react';
import { Tooltip } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import classNames from '../../../utils/class-names';
import iconMap from '../../../utils/icon-map';
import { SidebarNavItem } from '../../../types';
import { prepareStringForId } from '../../../utils/string-utility';

interface SidebarNavItemRendererProps {
  item: SidebarNavItem;
  isCollapsed: boolean;
  toggleItem: (itemName: string) => void;
  handleMouseEnter: (e: React.MouseEvent<HTMLElement>, item: SidebarNavItem) => void;
  handleMouseLeave: () => void;
  expandedItems: Record<string, boolean>;
}

const SidebarNavItemRenderer = ({
  item,
  isCollapsed,
  toggleItem,
  handleMouseEnter,
  handleMouseLeave,
  expandedItems,
}: SidebarNavItemRendererProps): ReactElement => {
  const Icon = iconMap[item.icon];

  return (
    <Tooltip
      title={item.name}
      placement="right"
      disableHoverListener={!isCollapsed || (isCollapsed && !!item.subItems)}
      disableFocusListener={!isCollapsed}
      disableTouchListener={!isCollapsed}
      classes={{
        tooltip: 'text-white bg-gray-900 opacity-80 rounded-md shadow-xl',
      }}
    >
      <a
        id={item.id}
        href={item.href || undefined}
        onClick={item.href ? undefined : () => toggleItem(item.name)}
        onMouseEnter={(e) => handleMouseEnter(e, item)}
        onMouseLeave={handleMouseLeave}
        className={classNames(
          item.current
            ? 'bg-slate-700 text-white font-medium'
            : 'text-slate-300 hover:bg-slate-900 hover:text-slate-200',
          isCollapsed ? 'w-10 overflow-hidden' : '',
          'nav-item group flex flex-1 items-center px-2 py-2 text-sm font-normal rounded-md whitespace-nowrap'
        )}
      >
        {Icon && (
          <Icon
            className={classNames(
              item.current
                ? 'text-blue-400'
                : 'text-slate-400 group-hover:text-slate-400',
              'mr-3 shrink-0 h-6 w-6'
            )}
            aria-hidden="true"
          />
        )}
        {!isCollapsed && item.name}
        {item.subItems && !isCollapsed && (
          <button className="focus:outline-none" id={`${prepareStringForId(item.name)}-expand-button`}>
            {expandedItems[item.name] ? (
              <ChevronUpIcon className="absolute h-5 w-5 text-slate-300 right-2 top-1/2 transform -translate-y-1/2" />
            ) : (
              <ChevronDownIcon className="absolute h-5 w-5 text-slate-300 right-2 top-1/2 transform -translate-y-1/2" />
            )}
          </button>
        )}
      </a>
    </Tooltip>
  );
};

export default SidebarNavItemRenderer;
